"use client";

import { InlineWidget } from "react-calendly";
import { useCalendlyEventListener } from "react-calendly";
import { useRouter, useSearchParams } from "next/navigation";
import InlineButton from "@/components/ui/inline-button";
import { useWindowSize } from "@/hooks/use-window-size";
import { useState } from "react";
import type { CalendlyEventPayload } from "@/types/calendly";

export interface CalendlyWidgetProps {
  calendlyUrl: string;
  redirectUrl?: string;
  heading?: string;
  subheading?: string;
  reminder?: string;
  phoneNumber?: string;
  showStartOverButton?: boolean;
  onStartOver?: () => void;
  passEventDataToRedirect?: boolean;
}

const CalendlyWidget: React.FC<CalendlyWidgetProps> = ({
  calendlyUrl,
  redirectUrl,
  heading = "Schedule Your Consultation",
  subheading = "We're here to help you find the perfect care solution",
  reminder = "Please select a date and time using the calendar below.",
  phoneNumber = "(844) 248-4102",
  showStartOverButton = false,
  onStartOver,
  passEventDataToRedirect = true,
}) => {
  const size = useWindowSize();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isProcessing, setIsProcessing] = useState(false);

  const finalPrefillName = searchParams.get("name");
  const finalPrefillEmail = searchParams.get("email");
  const finalRedirectUrl = redirectUrl;

  useCalendlyEventListener({
    onDateAndTimeSelected: (e) => {
      console.log("Data", e.data);
      console.log("Date and time selected:", e.data.payload);
    },
    onEventScheduled: async (e) => {
      console.log("Data", e.data);
      console.log("Event scheduled:", e.data.payload);

      if (finalRedirectUrl && !isProcessing) {
        setIsProcessing(true);

        try {
          let finalRedirectUrlWithParams = finalRedirectUrl;

          if (passEventDataToRedirect && e.data.payload) {
            const payload = e.data.payload as CalendlyEventPayload;
            const params = new URLSearchParams();

            // If we have an event URI, fetch only the schedule data
            if (payload.event?.uri) {
              try {
                const response = await fetch("/api/calendly/scheduled-event", {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({
                    eventUri: payload.event.uri,
                  }),
                });

                if (response.ok) {
                  const { event } = await response.json();

                  // Only pass the start_time - we'll parse it on the thank you page
                  if (event.start_time) {
                    params.set("start_time", event.start_time);
                  }
                } else {
                  console.error(
                    "Failed to fetch event details:",
                    await response.text()
                  );
                }
              } catch (fetchError) {
                console.error("Error fetching event details:", fetchError);
              }
            }

            const separator = finalRedirectUrl.includes("?") ? "&" : "?";
            finalRedirectUrlWithParams = `${finalRedirectUrl}${separator}${params.toString()}`;
          }

          router.push(finalRedirectUrlWithParams);
        } catch (error) {
          console.error("Error processing event scheduling:", error);
          // Fallback to redirect without params if there's an error
          router.push(finalRedirectUrl);
        } finally {
          setIsProcessing(false);
        }
      }
    },
  });

  const calendlyPrefill = {
    name: finalPrefillName || "",
    email: finalPrefillEmail || "",
  };

  const handleStartOver = () => {
    if (onStartOver) {
      onStartOver();
    }
  };

  return (
    <div className="flex w-full flex-col bg-transparent">
      <div className="flex w-full flex-col px-4 py-6 pb-10 md:px-6 md:py-8 md:pb-16 lg:mx-auto lg:max-w-lg lg:px-0 lg:py-10">
        {/* Thank you container */}
        <div className="flex flex-col gap-4">
          {/* Headers container */}
          <div className="flex flex-col gap-2 text-center lg:mx-auto lg:max-w-[722px]">
            <h1 className="w-full font-heading text-heading-4 text-secondary-500 md:text-heading-3">
              {heading}
            </h1>
            <h2 className="font-subheading text-subheading-3-500 font-medium text-neutral-500 md:text-subheading-3-500">
              {subheading}
            </h2>
          </div>

          {/* Reminder */}
          <p className="text-center font-body text-body-1-300 text-neutral-500">
            {reminder}
          </p>
        </div>

        {/* Gap between thank you container and calendly widget */}
        <div className="mt-6 flex justify-center md:mt-8 lg:mt-10">
          <div className="h-fit w-full overflow-hidden rounded-lg md:-mt-[66px]">
            <InlineWidget
              styles={{
                width: "100%",
                height:
                  size === "lg" ? "800px" : size === "md" ? "1120px" : "1050px",
              }}
              url={calendlyUrl}
              prefill={calendlyPrefill}
              pageSettings={{
                hideGdprBanner: true,
                hideLandingPageDetails: true,
              }}
            />
          </div>
        </div>

        {/* Gap between calendly widget and footer note */}
        <div className="mt-6 text-center font-body text-body-2-300 text-neutral-500 md:mt-0">
          If you have any urgent concerns, please don&apos;t hesitate to call us
          at{" "}
          <a
            href={`tel:${phoneNumber.replace(/[^\d]/g, "")}`}
            className="underline"
          >
            {phoneNumber}
          </a>
          .
        </div>

        {showStartOverButton && (
          <div className="mx-auto mt-6">
            <InlineButton
              size="md"
              onClick={handleStartOver}
            >
              Start over?
            </InlineButton>
          </div>
        )}

        {/* Processing indicator */}
        {isProcessing && (
          <div className="mx-auto mt-4 text-center">
            <p className="text-sm text-neutral-500">
              Processing your appointment...
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalendlyWidget;
